import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../hooks';
import { Button, Card, CardBody, Spinner, Alert } from '../../components/ui';
import { profileShareService } from '../../services/profileShareService';
import {
  formatDate,
  formatShareTimeRemaining,
  isShareExpired,
  buildShareUrl,
  openWhatsAppShare,
  SHARE_EXPIRY_OPTIONS,
  DEFAULT_SHARE_EXPIRY_DAYS,
} from '../../utils';
import type { ProfileShareSummary } from '../../types';

export const ManageShares: React.FC = () => {
  const { user } = useAuth();
  const [shares, setShares] = useState<ProfileShareSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copiedToken, setCopiedToken] = useState<string | null>(null);
  const [revokingToken, setRevokingToken] = useState<string | null>(null);
  const [editingToken, setEditingToken] = useState<string | null>(null);
  const [expiryDraft, setExpiryDraft] = useState(DEFAULT_SHARE_EXPIRY_DAYS);
  const [savingToken, setSavingToken] = useState<string | null>(null);

  const loadShares = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    setError(null);
    const { data, error: fetchError } = await profileShareService.getUserShares(user.id);
    if (fetchError) {
      setError(fetchError);
      setShares([]);
    } else {
      setShares(data);
    }
    setLoading(false);
  }, [user?.id]);

  useEffect(() => {
    loadShares();
  }, [loadShares]);

  const handleCopy = async (token: string) => {
    await navigator.clipboard.writeText(buildShareUrl(token));
    setCopiedToken(token);
    setTimeout(() => setCopiedToken(null), 2000);
  };

  const handleWhatsApp = (token: string) => {
    openWhatsAppShare(buildShareUrl(token), 'my');
  };

  const handleRevoke = async (token: string) => {
    if (!window.confirm('Cancel this share link? Anyone with it will lose access immediately.')) return;
    setRevokingToken(token);
    const { error: deleteError } = await profileShareService.deleteShare(token);
    setRevokingToken(null);
    if (deleteError) {
      setError(deleteError);
      return;
    }
    setShares(prev => prev.filter(s => s.token !== token));
    if (editingToken === token) setEditingToken(null);
  };

  const startEditExpiry = (share: ProfileShareSummary) => {
    setEditingToken(share.token);
    setExpiryDraft(DEFAULT_SHARE_EXPIRY_DAYS);
  };

  const handleSaveExpiry = async (token: string) => {
    setSavingToken(token);
    setError(null);
    const { expiresAt, error: updateError } = await profileShareService.updateShareExpiry(token, expiryDraft);
    setSavingToken(null);
    if (updateError || !expiresAt) {
      setError(updateError ?? 'Failed to update expiry.');
      return;
    }
    setShares(prev =>
      prev.map(s => (s.token === token ? { ...s, expires_at: expiresAt } : s)),
    );
    setEditingToken(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <Link to="/profile" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800 mb-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to profile
          </Link>
          <h1 className="text-2xl font-bold text-gray-900">Shared Links</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Links you have generated, how long they last, and options to cancel them.
          </p>
        </div>
        <Link to="/profile?share=1">
          <Button size="sm">Create new link</Button>
        </Link>
      </div>

      {error && (
        <Alert variant="danger" onClose={() => setError(null)}>{error}</Alert>
      )}

      {shares.length === 0 ? (
        <Card>
          <CardBody>
            <div className="text-center py-12">
              <h3 className="text-base font-bold text-gray-900 mb-1">No share links yet</h3>
              <p className="text-sm text-gray-500 mb-5 max-w-sm mx-auto">
                Generate a share link from your Passport profile to send it to employers or contacts.
              </p>
              <Link to="/profile?share=1">
                <Button>Create share link</Button>
              </Link>
            </div>
          </CardBody>
        </Card>
      ) : (
        <div className="space-y-3">
          {shares.map(share => {
            const expired = isShareExpired(share.expires_at);
            const url = buildShareUrl(share.token);
            const isEditing = editingToken === share.token;

            return (
              <Card key={share.id}>
                <CardBody>
                  <div className="flex flex-col gap-3">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-sm font-semibold text-gray-900 break-all">
                            {url.replace(/^https?:\/\//, '')}
                          </p>
                          <span
                            className={`text-xs px-2 py-0.5 rounded-full font-semibold border ${
                              expired
                                ? 'bg-red-50 text-red-700 border-red-200'
                                : 'bg-emerald-50 text-emerald-700 border-emerald-200'
                            }`}
                          >
                            {expired ? 'Expired' : formatShareTimeRemaining(share.expires_at)}
                          </span>
                        </div>
                        <p className="text-xs text-gray-400 mt-1">
                          Created {formatDate(share.created_at)} · Expires {formatDate(share.expires_at)}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          Fields: {share.selected_fields.join(', ').replace(/_/g, ' ')}
                        </p>
                      </div>
                    </div>

                    {isEditing && (
                      <div className="flex flex-col sm:flex-row sm:items-end gap-2 p-3 bg-gray-50 border border-gray-100 rounded-xl">
                        <div className="flex-1">
                          <label
                            htmlFor={`expiry-${share.token}`}
                            className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5"
                          >
                            New duration from today
                          </label>
                          <select
                            id={`expiry-${share.token}`}
                            value={expiryDraft}
                            onChange={e => setExpiryDraft(Number(e.target.value))}
                            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-brand-500"
                          >
                            {SHARE_EXPIRY_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            loading={savingToken === share.token}
                            onClick={() => handleSaveExpiry(share.token)}
                          >
                            Save
                          </Button>
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => setEditingToken(null)}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    )}

                    <div className="flex flex-wrap gap-2">
                      {!expired && (
                        <>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => handleCopy(share.token)}
                          >
                            {copiedToken === share.token ? 'Copied' : 'Copy link'}
                          </Button>
                          <Button
                            size="sm"
                            className="!bg-[#25D366] hover:!bg-[#1ebe57] !border-transparent"
                            onClick={() => handleWhatsApp(share.token)}
                          >
                            WhatsApp
                          </Button>
                        </>
                      )}
                      {!isEditing && (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => startEditExpiry(share)}
                        >
                          {expired ? 'Renew expiry' : 'Edit expiry'}
                        </Button>
                      )}
                      <Button
                        variant="danger"
                        size="sm"
                        loading={revokingToken === share.token}
                        onClick={() => handleRevoke(share.token)}
                      >
                        {expired ? 'Remove' : 'Cancel link'}
                      </Button>
                    </div>
                  </div>
                </CardBody>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};
